import { compose, withStateHandlers, withHandlers } from 'recompose';
import { connect } from 'react-redux';
import { Form } from 'antd';
import * as R from 'ramda';

import { debounceTime } from '../constants';
import { fetchContacts } from '../../api/apiko-api';
import { closeCurrentModal } from '../modal-reducer';
import { getFullRequestById } from '../../redux/selectors';
import { MeetingNotesModal as Component } from './component';
import { debounce, getMeetingDateByDateAndTime } from '../../utils';
import { filterUsers } from '../manage-users-modal/list-users/utils';
import { handleCreateMeetingNote } from '../../redux/thunk/meeting-notes';
import {
  handleTaskDelete,
  handleTaskUpdate,
  handleTaskCreate,
} from '../../redux/thunk/tasks';

const debouncedFetch = debounce(fetchContacts, debounceTime);


const mapStateToProps = (state, { payload: { requestId } }) => {
  const {
    tasks: { isCreating, isUpdating, isFetching },
  } = state;

  return ({
    request: getFullRequestById(state, { requestId }),
    users: filterUsers(R.pathOr({}, ['users', 'items'], state)),
    isLoadingTask: isCreating || isUpdating || isFetching,
  });
};

export const AddMeetingNotesModal = Form.create()(compose(
  connect(
    mapStateToProps,
    {
      closeCurrentModal,
      handleCreateMeetingNote,
      handleTaskDelete,
      handleTaskUpdate,
      handleTaskCreate,
    },
  ),
  withStateHandlers(
    () => ({
      isShowContacts: false,
      isShowCreateTaskForm: false,
      isSearchingParticipants: false,
      tasks: [],
      participantsOptions: {
        contacts: [],
        users: [],
      },
      // it is needed for splitting users and clients on submit form
      foundedParticipantsBuffer: {
        contacts: [],
        users: [],
      },
    }),
    {
      onToggleShowContacts: ({ isShowContacts }) => () => ({ isShowContacts: !isShowContacts }),
      onToggleCreateTaskForm: ({ isShowCreateTaskForm }) => () => ({
        isShowCreateTaskForm: !isShowCreateTaskForm,
      }),
      onDeleteTask: ({ tasks }) => taskId => ({
        tasks: tasks.filter(item => item._id !== taskId),
      }),
      onToggleEditTaskForm: ({ tasks }) => taskId => ({
        tasks: tasks.map(item =>
          item._id === taskId ? { ...item, isEdit: !item.isEdit } : item),
      }),
      onCreateTaskForm: ({ tasks }) => task => ({
        tasks: [...tasks, task],
        isShowCreateTaskForm: false,
      }),
      onUpdateTaskForm: ({ tasks }) => task => ({
        tasks: tasks.map(item => item._id === task._id
          ? { ...item, ...task, isEdit: false }
          : item),
      }),
      setIsSearchingParticipants: () => isSearchingParticipants => ({ isSearchingParticipants }),
      setParticipantsOptions: () => ({ contacts = [], users = [] }) => ({
        participantsOptions: {
          contacts,
          users,
        },
      }),
      setFoundedParticipantsBuffer: ({
        foundedParticipantsBuffer,
      }) => ({
        contacts = [],
        users = [],
      }) => ({
        foundedParticipantsBuffer: {
          contacts: [...new Set([...foundedParticipantsBuffer.contacts, ...contacts.map(R.prop('_id'))])],
          users: [...new Set([...foundedParticipantsBuffer.users, ...users.map(R.prop('_id'))])],
        },
      }),
    },
  ),
  withHandlers({
    onParticipantSearch: props => (value) => {
      if (value.length > 1 && value.trim()[0] !== '@') {
        props.setIsSearchingParticipants(true);
        debouncedFetch({ searchTerm: value, simpleList: true })
          .then(({ data }) => {
            const participants = {
              contacts: data.contacts,
              users: Object.values(props.users).filter(({ fullName, email }) => {
                const normalizedValue = value.trim().toLowerCase();
                return fullName.trim().toLowerCase().includes(normalizedValue)
                  || email.trim().toLowerCase().includes(normalizedValue);
              }),

            };

            props.setIsSearchingParticipants(false);
            props.setParticipantsOptions(participants);
            props.setFoundedParticipantsBuffer(participants);
          });
      } else props.setParticipantsOptions({ contacts: [], users: [] });
    },
    onMeetingNoteCreate: ({
      form,
      tasks,
      request,
      foundedParticipantsBuffer,
      ...actions
    }) => (event) => {
      event.preventDefault();
      form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          const data = {
            ...(R.omit(['date', 'time', 'participants'], values)),
            requestId: request._id,
            participants: {
              users: R.intersection(values.participants, foundedParticipantsBuffer.users),
              contacts: R.intersection(values.participants, foundedParticipantsBuffer.contacts),
            },
            meetingDate: getMeetingDateByDateAndTime(values.date, values.time),
            meetingTasks: tasks.map(R.prop('_id')),
          };

          actions.handleCreateMeetingNote(data)
            .then(() => actions.closeCurrentModal());
        } else {
          console.error(err);
        }
      });
    },
    onDeleteTask: ({ onDeleteTask, ...actions }) => (taskId) => {
      actions.handleTaskDelete(taskId)
        .then(() => onDeleteTask(taskId));
    },
    onCreateTaskForm: ({ onCreateTaskForm, ...actions }) => (task) => {
      actions.handleTaskCreate(task)
        .then(({ payload }) => onCreateTaskForm(payload));
    },
    onUpdateTaskForm: ({ onUpdateTaskForm, ...actions }) => (task) => {
      actions.handleTaskUpdate(task._id, task)
        .then(payload => onUpdateTaskForm(payload));
    },
    onAssignMeClick: ({
      form: { getFieldValue, setFieldsValue },
      users, participantsOptions, setParticipantsOptions, setFoundedParticipantsBuffer,
    }) => (userId) => {
      const [{ _id, fullName }] = users.filter(user => user._id === userId);

      if (_id && fullName) {
        setFieldsValue({
          participants: [...new Set([...(getFieldValue('participants') || []), _id])],
        });

        setFoundedParticipantsBuffer({ users: [{ _id, fullName }] });
        setParticipantsOptions({
          ...participantsOptions,
          users: [...new Set([...participantsOptions.users, { _id, fullName }])],
        });
      }
    },
  }),
)(Component));



// WEBPACK FOOTER //
// ./src/modals/add-meeting-notes-modal/container.js